import React, { FC, useEffect, useState } from "react";
import SpinnerOverlay from "../../../elements/Spinner/SpinnerOverlay";
import DeviceTableRow from "../../../models/DeviceTableRow";
import { isLockType } from "../../../models/DeviceTypeEnum";
import ConnectService from "../../../services/connect.service";
import SmartHomeService from "../../../services/smarthome.service";
import { PropertyStore } from "../../../store/property";
import { TurnKeyService } from "../../../services/turnkey.service";
import ErrorBadge from "../../../elements/Error/ErrorBadge";

import styles from "./RemoveDeviceDialog.module.scss";
import { AppContextStore } from "../../../store/app-context";

interface RemoveDeviceDialogProps {
  dataRow: DeviceTableRow;
  tableData?: DeviceTableRow[];
  onClose?: () => void;
  onSave?: (deviceId: number, gatewayId: number) => void;
  operator?: string;
}

const RemoveDeviceDialog: FC<RemoveDeviceDialogProps> = (props) => {
  const dataRow = props.dataRow;
  const tableData = props.tableData;

  // Safely get property data - return null if not available (shouldn't happen but safety check)
  const property = PropertyStore.selectors.usePropertyData();
  const propertyId = property?.id;
  const origin = AppContextStore.selectors.useAppOrigin();
  const originFromAdmin = origin === "admin" ? true : false;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [reservationCount, setReservationCount] = useState<number>();
  const [isTurnkeyLock, setIsTurnkeyLock] = useState<boolean>();

  // Early return if property is not available (shouldn't happen but safety check)
  if (!propertyId) {
    return (
      <div className={styles.DialogContent}>
        <div className="title">Error: Property data not available</div>
      </div>
    );
  }

  // Load the current reservation count for this unit
  useEffect(() => {
    const isLock = isLockType(dataRow.typeId);
    const isTurnkey =
      isLockType(dataRow.typeId) &&
      dataRow.vendor?.toLowerCase().startsWith("turnkey");
    setIsTurnkeyLock(isTurnkey);

    let isLockReplaceable = false;
    if (isLock) {
      const otherActiveLocks =
        tableData?.filter(
          (x) =>
            x.active &&
            isLockType(x.typeId) &&
            x.getDeviceId() !== dataRow.getDeviceId(),
        ).length ?? 0;

      // always allow lock to be deleted if it is inactive and other active locks exist
      isLockReplaceable = otherActiveLocks > 0 && !dataRow.active;
    }

    const abortController = new AbortController();
    const connectService = new ConnectService();

    // All non-turnkey locks must check for future reservations before being deleted...
    // ...UNLESS it is inactive and another active lock exists

    if (isLock && !isTurnkey && originFromAdmin) {
      if (isLockReplaceable) {
        setReservationCount(0);
      } else {
        setIsLoading(true);
        setError(undefined);
        // Due Admin sunset, this request should be removed or replaced
        connectService
          .getReservationCount(propertyId, abortController.signal)
          .then((reservationCount) => setReservationCount(reservationCount))
          .catch((err) => {
            console.error("Failed to load reservation count from Connect", err);
            setReservationCount(0); // Assume 0 reservations if connect endpoint crashes, currently reservation validation is just a warning not a blocker
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } else {
      setReservationCount(0);
    }

    return () => {
      abortController.abort();
    };
  }, [propertyId, dataRow, tableData, originFromAdmin]);

  const handleClose = function () {
    props.onClose?.();
  };

  const handleRemoveDevice = function () {
    if (isTurnkeyLock) removeTurnKeyLock();
    else removeDevice();
  };

  const removeDevice = function () {
    const smartHomeService = new SmartHomeService();
    const [deviceId, gatewayId] = [
      props.dataRow.getDeviceId()!,
      props.dataRow.getGatewayId()!,
    ];

    setIsLoading(true);
    setError(undefined);
    smartHomeService
      .deleteDeviceAndGateway(deviceId, gatewayId, props.operator)
      .then((success) => {
        if (success) {
          props.onSave?.(deviceId, gatewayId);
        } else {
          setError(new Error("Unable to remove device"));
        }
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const removeTurnKeyLock = function () {
    const turnKeyService = new TurnKeyService();
    const [deviceId, gatewayId, externalId] = [
      props.dataRow.getDeviceId()!,
      props.dataRow.getGatewayId()!,
      props.dataRow.externalId!,
    ];

    setIsLoading(true);
    setError(undefined);

    turnKeyService
      .teardownLock(externalId, gatewayId, deviceId, props.operator)
      .then((success) => {
        if (success) props.onSave?.(deviceId, gatewayId);
        else setError(new Error("Unable to remove device. Please try again."));
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  };

  const getMainContent = () => {
    if (reservationCount !== 0) {
      return (
        <div className="title-message">
          This property has future reservations. Are you sure you want to remove
          this {props.dataRow.type || "device"}?
        </div>
      );
    } else {
      return (
        <div className="title">
          Are you sure you want to remove this {props.dataRow.type || "device"}?
        </div>
      );
    }
  };

  return (
    <div className={styles.DialogContent}>
      {getMainContent()}
      <div className="action">
        <div className="cancel">
          <button
            id="btn-cancel-remove"
            className="btn-alt"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>
        <div className="remove">
          <button
            id="btn-commit-remove"
            className="btn-alert"
            onClick={handleRemoveDevice}
          >
            Remove
          </button>
        </div>
      </div>
      <ErrorBadge error={error} />
      {isLoading && <SpinnerOverlay />}
    </div>
  );
};

export default RemoveDeviceDialog;
